import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { formatStageError } from "../lib/format";
import { reviewGame, ReviewError, REVIEW_MODES } from "../lib/gameReview";
import { isLikelyPhone } from "../lib/device";
import { getEngine } from "../lib/browserEngine";

const DEMO_PGN = `[Event "Casual"]
[Site "?"]
[Date "2024.01.01"]
[White "Beth"]
[Black "Borgov"]
[Result "*"]

1. e4 e5 2. Nf3 Nc6 3. Bb5 a6 4. Ba4 Nf6 5. O-O Be7 6. Re1 b5 7. Bb3 d6 8. c3 O-O 9. h3 Nb8 10. d4 Nbd7 11. c4 c6 12. cxb5 axb5 13. Nc3 Bb7 14. Bg5 b4 15. Nb1 h6 16. Bh4 c5 17. dxe5 Nxe4 18. Bxe7 Qxe7 19. exd6 Qf6 20. Nbd2 Nxd6 21. Nc4 Nxc4 22. Bxc4 Nb6 23. Ne5 Rae8 24. Bxf7+ *`;

const MAX_WAIT_MS = 3 * 60 * 1000;
const STAGE_TEXT = {
  loading: "Loading the chess engine on your device…",
  scan: "Checking every move",
  deep: "Re-checking flagged moves",
  coach: "Coach is writing your review…",
};

const ReviewProgress = ({ progress, elapsed, onCancel }) => {
  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;
  const counted = progress.stage === "scan" || progress.stage === "deep";
  return (
    <div className="card-panel p-4 space-y-3" data-testid="review-progress">
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="text-[#EFE8D8]" data-testid="review-progress-stage">
          {STAGE_TEXT[progress.stage]}{progress.stage === "deep" ? ` at depth ${progress.depth}` : ""}{counted ? ` (${progress.done}/${progress.total})` : ""}
        </span>
        <span className="font-mono text-xs text-[#756F67]" data-testid="review-progress-elapsed">{elapsed}s</span>
      </div>
      <div className="h-1.5 bg-[rgba(239,232,216,0.08)] rounded-sm overflow-hidden">
        <div className="h-full bg-[#C6A15B] transition-[width] duration-300" style={{ width: `${progress.stage === "coach" ? 100 : pct}%` }} />
      </div>
      <p className="text-xs text-[#A8A196]" data-testid="review-keep-open-warning">
        The engine runs on your device — keep this page open and your screen on until the review is ready. Leaving cancels it.
      </p>
      {onCancel && <button type="button" onClick={onCancel} className="btn-ghost text-sm" data-testid="review-cancel">Cancel review</button>}
    </div>
  );
};

async function waitForCoach(jobId, alive) {
  const started = Date.now();
  while (Date.now() - started < MAX_WAIT_MS) {
    await new Promise((r) => setTimeout(r, 2500));
    if (!alive.current) return null;
    let job;
    try {
      job = (await api.get(`/analyze/jobs/${jobId}`)).data;
    } catch (pollErr) {
      const st = pollErr.response?.status;
      if (st && st < 500) throw pollErr;
      continue;
    }
    if (job.status !== "running") return job;
  }
  return { status: "error", stage: "llm", message: "The coach is taking unusually long — check Recent analyses on your dashboard shortly." };
}

const MODE_OPTIONS = [
  { key: "quick", label: "Quick", hint: `About twice as fast — re-checks your ${REVIEW_MODES.quick.maxTargets} worst mistakes at depth ${REVIEW_MODES.quick.deepDepth}.` },
  { key: "thorough", label: "Thorough", hint: `Re-checks up to ${REVIEW_MODES.thorough.maxTargets} flagged mistakes at depth ${REVIEW_MODES.thorough.deepDepth}.` },
];

const ModePicker = ({ mode, setMode, disabled }) => (
  <div>
    <label className="block text-xs uppercase tracking-widest text-[#756F67] mb-2 font-mono">Review depth</label>
    <div className="flex gap-2">
      {MODE_OPTIONS.map((o) => (
        <button
          key={o.key}
          type="button"
          data-testid={`review-mode-${o.key}`}
          aria-pressed={mode === o.key}
          onClick={() => setMode(o.key)}
          disabled={disabled}
          className={`px-4 py-2 border rounded-sm font-mono text-sm transition-colors ${
            mode === o.key ? "border-[#C6A15B] text-[#C6A15B] bg-[#C6A15B]/10" : "border-[rgba(239,232,216,0.15)] text-[#A8A196] hover:text-[#EFE8D8]"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
    <p className="text-xs text-[#756F67] mt-2" data-testid="review-mode-hint">
      {MODE_OPTIONS.find((o) => o.key === mode).hint}
    </p>
  </div>
);

const asStageError = (stage, message) => formatStageError({ response: { data: { stage, message } } }, message);

export default function Analyze() {
  const [pgn, setPgn] = useState("");
  const [color, setColor] = useState("white");
  const [mode, setMode] = useState(() => (isLikelyPhone() ? "quick" : "thorough"));
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const [progress, setProgress] = useState({ stage: "loading", done: 0, total: 1 });
  const alive = useRef(true);
  const cancelled = useRef(false);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; cancelled.current = true; getEngine().stop(); };
  }, []);
  const nav = useNavigate();

  useEffect(() => {
    if (!loading) return undefined;
    const started = Date.now();
    const tick = setInterval(() => setElapsed(Math.round((Date.now() - started) / 1000)), 1000);
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => { clearInterval(tick); window.removeEventListener("beforeunload", warn); };
  }, [loading]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    setElapsed(0);
    setProgress({ stage: "loading", done: 0, total: 1 });
    cancelled.current = false;
    try {
      const result = await reviewGame(pgn, color, {
        mode,
        onProgress: (p) => alive.current && setProgress(p),
        isCancelled: () => cancelled.current,
      });
      setProgress({ stage: "coach", done: 1, total: 1 });
      const { data } = await api.post("/analyze", { pgn, color, ...result });
      const job = await waitForCoach(data.job_id, alive);
      if (!job || !alive.current) return;
      if (job.status === "done") return nav(`/analyze/${job.analysis_id}`);
      setErr(asStageError(job.stage, job.message));
    } catch (e) {
      if (e instanceof ReviewError) {
        if (e.stage !== "cancelled") setErr(asStageError(e.stage, e.message));
      } else {
        setErr(formatStageError(e, "Analysis failed — please try again."));
      }
    } finally {
      if (alive.current) setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
      <div className="overline mb-2">Analyse my game</div>
      <h1 className="font-serif text-3xl sm:text-4xl text-[#EFE8D8] mb-3">Paste. Wait. Learn.</h1>
      <p className="text-[#A8A196] mb-8 max-w-2xl">
        Stockfish runs right in your browser and flags every inaccuracy; a coach explains the three worst moments in plain English.
      </p>

      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="block text-xs uppercase tracking-widest text-[#756F67] mb-2 font-mono">Your color</label>
          <div className="flex gap-2">
            {["white", "black"].map((c) => (
              <button
                key={c}
                type="button"
                data-testid={`analyze-color-${c}`}
                onClick={() => setColor(c)}
                disabled={loading}
                className={`px-4 py-2 border rounded-sm font-mono text-sm transition-colors ${
                  color === c ? "border-[#C6A15B] text-[#C6A15B] bg-[#C6A15B]/10" : "border-[rgba(239,232,216,0.15)] text-[#A8A196] hover:text-[#EFE8D8]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <ModePicker mode={mode} setMode={setMode} disabled={loading} />

        <div>
          <label className="block text-xs uppercase tracking-widest text-[#756F67] mb-2 font-mono">PGN</label>
          <textarea
            disabled={loading}
            data-testid="analyze-pgn"
            value={pgn}
            onChange={(e) => setPgn(e.target.value)}
            required
            rows={14}
            placeholder={DEMO_PGN}
            className="input-ink font-mono text-sm resize-y min-h-[300px]"
          />
        </div>

        {err && (
          <div data-testid="analyze-error" className="text-sm text-[#A04A3C]">
            {String(err)}
          </div>
        )}

        {loading && (
          <ReviewProgress
            progress={progress}
            elapsed={elapsed}
            onCancel={progress.stage === "coach" ? null : () => { cancelled.current = true; getEngine().stop(); }}
          />
        )}

        <div className="flex flex-wrap items-center gap-3">
          <button data-testid="analyze-submit" type="submit" disabled={loading} className="btn-brass">
            {loading ? "Analysing…" : "Run engine + coach"}
          </button>
          <button type="button" onClick={() => setPgn(DEMO_PGN)} disabled={loading} className="btn-ghost" data-testid="analyze-load-sample">Load sample PGN</button>
        </div>
      </form>
    </div>
  );
}
